import React from "react";
import { useWatch } from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  LabelRow,
} from "../../../../components/FormPanel";
import {
  FormMoneyInput,
  InputDate,
  SelectForm,
} from "../../../../components/Input";
import { localToday, toIsoDate } from "../../../../utils/dates";
import {
  validateOverrideDate,
  validateOverrideFxRate,
  validateOverridePrice,
} from "../overrideValidation";
import { DATE_PLACEHOLDER, PRICE_OVERRIDE_POLICY } from "./constants";
import { OverrideList } from "./OverrideList";
import { PriceOverrideFieldsProps } from "./types";
import { Policy, ReplacementNote, StaticInstrument } from "./styles";

export const PriceOverrideFields: React.FC<PriceOverrideFieldsProps> = ({
  control,
  instrumentOptions,
  defaultInstrument,
  selectedInstrument,
  overrides,
  isLoadingOverrides,
  isWriteInFlight,
  showFxRate,
  isInstrumentFixed,
  policyLabel,
  onDelete,
}) => {
  const selectedDate = useWatch({ control, name: "date" });
  const nativeCurrency = selectedInstrument?.instrument.nativeCurrency ?? null;
  const priceLabel = `Price (${nativeCurrency ?? "native currency"})`;
  const dateText = selectedDate ? toIsoDate(selectedDate) : null;
  const replacement = dateText
    ? (overrides.find((override) => override.date === dateText) ?? null)
    : null;

  const policyHeading = policyLabel ? (
    <FieldLabel as="span">{policyLabel}</FieldLabel>
  ) : null;

  const instrumentField = isInstrumentFixed ? (
    <Field>
      <LabelRow>
        <FieldLabel as="span">Instrument</FieldLabel>
      </LabelRow>
      <StaticInstrument>{selectedInstrument?.label}</StaticInstrument>
    </Field>
  ) : (
    <Field>
      <LabelRow>
        <FieldLabel htmlFor="price-override-instrument">Instrument</FieldLabel>
      </LabelRow>
      <SelectForm
        id="price-override-instrument"
        name="instrument"
        control={control}
        defaultValue={defaultInstrument}
        options={instrumentOptions}
        disabled={isWriteInFlight}
        rules={{ required: "Instrument is required" }}
      />
    </Field>
  );

  return (
    <>
      <FormCard>
        {policyHeading}
        <Policy>{PRICE_OVERRIDE_POLICY}</Policy>
      </FormCard>
      <FormCard>
        {instrumentField}
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="price-override-date">Price date</FieldLabel>
          </LabelRow>
          <InputDate
            id="price-override-date"
            name="date"
            control={control}
            rules={{ validate: validateOverrideDate }}
            disabled={isWriteInFlight}
            maxDate={localToday()}
            placeholder={DATE_PLACEHOLDER}
          />
        </Field>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="price-override-native-price">
              {priceLabel}
            </FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="price-override-native-price"
            name="priceNative"
            control={control}
            rules={{ validate: validateOverridePrice }}
            disabled={isWriteInFlight}
            placeholder="0.00"
          />
        </Field>
        {showFxRate && (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="price-override-fx-rate">
                FX rate to EUR
              </FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="price-override-fx-rate"
              name="fxToEur"
              control={control}
              rules={{ validate: validateOverrideFxRate }}
              disabled={isWriteInFlight}
              placeholder="Optional when PMA can find a rate"
            />
            <FieldHelper>
              Enter a rate only when automatic conversion is unavailable.
            </FieldHelper>
          </Field>
        )}
        {replacement && (
          <ReplacementNote role="note">
            Saving will replace the existing {replacement.priceNative}{" "}
            {nativeCurrency} price for {replacement.date}.
          </ReplacementNote>
        )}
      </FormCard>
      <OverrideList
        overrides={overrides}
        nativeCurrency={nativeCurrency}
        isLoading={isLoadingOverrides}
        isDisabled={isWriteInFlight}
        onDelete={onDelete}
      />
    </>
  );
};
